import { create } from 'zustand';
import {
  asNullableString,
  asString,
  normalizeChatMessage,
  sanitizeChatMessagePatch,
} from './normalizers';
import { mergeChatRoomList } from './room-merge';
import type { ChatMessage, ChatRoom } from './types';

export function chatMessageIdEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  const na = typeof a === 'number' ? a : Number(a);
  const nb = typeof b === 'number' ? b : Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  return String(a) === String(b);
}

export function asChatMessageList(v: unknown): ChatMessage[] {
  return Array.isArray(v) ? (v as ChatMessage[]) : [];
}

export function asChatRoomList(v: unknown): ChatRoom[] {
  return Array.isArray(v) ? (v as ChatRoom[]) : [];
}

function asTypingList(v: unknown): { user_id: string; user_name: string }[] {
  return Array.isArray(v) ? (v as { user_id: string; user_name: string }[]) : [];
}

function normalizeRoomPreviewMessage(
  message: Pick<ChatMessage, 'content' | 'created_at'> & {
    sender_name?: string;
    message_type?: string;
    id?: number | string;
    sender_user_id?: number | string | null;
    is_read?: boolean | null;
  }
): Required<Pick<ChatRoom, 'last_message'>>['last_message'] {
  const idRaw = (message as { id?: unknown }).id;
  const sidRaw = (message as { sender_user_id?: unknown }).sender_user_id;
  const idNum = idRaw != null && idRaw !== '' ? Number(idRaw) : undefined;
  const sidNum = sidRaw != null && sidRaw !== '' ? Number(sidRaw) : null;
  const base: Required<Pick<ChatRoom, 'last_message'>>['last_message'] = {
    content: asString(message.content),
    created_at: asString(message.created_at) || new Date().toISOString(),
    sender_name: asString(message.sender_name),
    message_type: asString(message.message_type),
  };
  if (idNum != null && Number.isFinite(idNum)) {
    (base as { id?: number }).id = idNum;
  }
  if (sidNum != null && Number.isFinite(sidNum)) {
    (base as { sender_user_id?: number }).sender_user_id = sidNum;
  } else if (sidRaw === null) {
    (base as { sender_user_id?: number | null }).sender_user_id = null;
  }
  if (typeof (message as { is_read?: unknown }).is_read === 'boolean') {
    (base as { is_read?: boolean }).is_read = (message as { is_read: boolean }).is_read;
  }
  return base;
}

function normalizeStoreMessage(roomId: number, message: ChatMessage, source: string): ChatMessage | null {
  return normalizeChatMessage(message, {
    source,
    fallbackRoomId: roomId,
    allowMissingRoomId: true,
  }) as ChatMessage | null;
}

function normalizeStoreMessagePatch(patch: Partial<ChatMessage>): Partial<ChatMessage> {
  const safePatch = sanitizeChatMessagePatch(patch) as Partial<ChatMessage>;
  if ('is_edited' in patch) safePatch.is_edited = Boolean((patch as { is_edited?: unknown }).is_edited);
  if ('link_preview' in patch) {
    safePatch.link_preview =
      patch.link_preview && typeof patch.link_preview === 'object' ? patch.link_preview : null;
  }
  if ('deleted_at' in patch) {
    safePatch.deleted_at = asNullableString((patch as { deleted_at?: unknown }).deleted_at);
  }
  return safePatch;
}

export interface ChatState {
  rooms: ChatRoom[];
  activeRoomId: number | null;
  activeForumTopicId: number | null;
  messages: Record<number | string, ChatMessage[]>;
  typingUsers: Record<number | string, { user_id: string; user_name: string }[]>;
  addMessage: (roomId: number | string, message: ChatMessage) => void;
  setMessages: (roomId: number | string, messages: ChatMessage[]) => void;
  setRooms: (rooms: ChatRoom[]) => void;
  mergeRooms: (rooms: ChatRoom[]) => void;
  updateRoomLastRead: (roomId: number, lastReadMessageId: number | null) => void;
  touchRoom: (
    roomId: number,
    message: Pick<ChatMessage, 'content' | 'created_at'> & {
      sender_name?: string;
      message_type?: string;
      id?: number | string;
      sender_user_id?: number | string | null;
      is_read?: boolean | null;
    },
    options?: { incrementUnread?: boolean; avatarUrl?: string | null }
  ) => void;
  setActiveRoom: (roomId: number | null) => void;
  setActiveForumTopicId: (topicId: number | null) => void;
  resetUnread: (roomId: number) => void;
  incrementUnread: (roomId: number) => void;
  updateMessage: (roomId: number, messageId: number | string, patch: Partial<ChatMessage>) => void;
  removeMessage: (roomId: number, messageId: number | string) => void;
  removePendingFromSender: (roomId: number, senderUserId: number | string) => void;
  setTyping: (roomId: number, userId: string, userName: string, typing: boolean) => void;
  clearTyping: (roomId?: number | null) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  rooms: [],
  activeRoomId: null,
  activeForumTopicId: null,
  messages: {},
  typingUsers: {},
  addMessage: (roomId, message) =>
    set((state) => {
      const id = Number(roomId);
      if (!Number.isFinite(id) || id < 1) return state;
      const normalized = normalizeStoreMessage(id, message, 'store.addMessage');
      if (!normalized) return state;
      const current = asChatMessageList(state.messages[id]);
      if (current.some((item) => chatMessageIdEqual(item.id, normalized.id))) {
        return state;
      }
      return {
        messages: {
          ...state.messages,
          [id]: [...current, normalized],
        },
      };
    }),
  setMessages: (roomId, messages) =>
    set((state) => {
      const id = Number(roomId);
      if (!Number.isFinite(id) || id < 1) return state;
      const list = Array.isArray(messages)
        ? messages
            .map((message) => normalizeStoreMessage(id, message, 'store.setMessages'))
            .filter((message): message is ChatMessage => message != null)
        : [];
      return {
        messages: {
          ...state.messages,
          [id]: list,
        },
      };
    }),
  setRooms: (rooms) => set({ rooms: Array.isArray(rooms) ? rooms : [] }),
  mergeRooms: (incoming) =>
    set((state) => ({
      rooms: mergeChatRoomList(Array.isArray(incoming) ? incoming : [], asChatRoomList(state.rooms)),
    })),
  updateRoomLastRead: (roomId, lastReadMessageId) =>
    set((state) => ({
      rooms: asChatRoomList(state.rooms).map((r) =>
        r.id === roomId ? { ...r, last_read_message_id: lastReadMessageId } : r
      ),
    })),
  touchRoom: (roomId, message, options) =>
    set((state) => {
      const rooms = asChatRoomList(state.rooms);
      const room = rooms.find((item) => item.id === roomId);
      if (!room) {
        return state;
      }

      const updatedRoom: ChatRoom = {
        ...room,
        last_message: normalizeRoomPreviewMessage(message),
        unread_count: options?.incrementUnread ? (room.unread_count || 0) + 1 : room.unread_count,
        avatar_url: options?.avatarUrl ?? room.avatar_url,
      };

      return {
        rooms: [updatedRoom, ...rooms.filter((item) => item.id !== roomId)],
      };
    }),
  setActiveRoom: (activeRoomId) =>
    set((state) => ({
      activeRoomId,
      activeForumTopicId:
        activeRoomId == null
          ? null
          : state.activeRoomId !== activeRoomId
            ? null
            : state.activeForumTopicId,
    })),
  setActiveForumTopicId: (activeForumTopicId) => set({ activeForumTopicId }),
  resetUnread: (roomId) =>
    set((state) => ({
      rooms: asChatRoomList(state.rooms).map((r) =>
        r.id === roomId ? { ...r, unread_count: 0 } : r
      ),
    })),
  incrementUnread: (roomId) =>
    set((state) => ({
      rooms: asChatRoomList(state.rooms).map((r) =>
        r.id === roomId ? { ...r, unread_count: (r.unread_count || 0) + 1 } : r
      ),
    })),
  updateMessage: (roomId, messageId, patch) =>
    set((state) => {
      const rid = Number(roomId);
      if (!Number.isFinite(rid) || rid < 1) return state;
      const safePatch = normalizeStoreMessagePatch(patch);
      return {
        messages: {
          ...state.messages,
          [rid]: asChatMessageList(state.messages[rid]).map((m) =>
            chatMessageIdEqual(m.id, messageId) ? { ...m, ...safePatch } : m
          ),
        },
      };
    }),
  removeMessage: (roomId, messageId) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: asChatMessageList(state.messages[roomId]).filter((m) => !chatMessageIdEqual(m.id, messageId)),
      },
    })),
  removePendingFromSender: (roomId, senderUserId) =>
    set((state) => {
      const rid = Number(roomId);
      if (!Number.isFinite(rid) || rid < 1) return state;
      const list = asChatMessageList(state.messages[rid]);
      const idx = list.findIndex(
        (m) =>
          (m as ChatMessage & { _pending?: boolean })._pending &&
          String(m.sender_user_id ?? m.sender_id) === String(senderUserId)
      );
      if (idx < 0) return state;
      return {
        messages: {
          ...state.messages,
          [rid]: list.filter((_, i) => i !== idx),
        },
      };
    }),
  setTyping: (roomId, userId, userName, typing) =>
    set((state) => {
      const current = asTypingList(state.typingUsers[roomId]);
      const next = typing
        ? current.some((t) => t.user_id === userId)
          ? current
          : [...current, { user_id: userId, user_name: userName }]
        : current.filter((t) => t.user_id !== userId);
      return {
        typingUsers: { ...state.typingUsers, [roomId]: next },
      };
    }),
  clearTyping: (roomId) =>
    set((state) => {
      if (roomId === undefined || roomId === null) {
        return { typingUsers: {} };
      }
      const next = { ...state.typingUsers };
      delete next[roomId];
      return { typingUsers: next };
    }),
}));
